/**
 * PinchZoomer Initialization
 *
 * This file sets up dependencies and loads the PinchZoomer plugin.
 * The plugin auto-initializes elements with data-elem="pinchzoomer".
 */

import Hammer from 'hammerjs';
import { gsap, Power0, Power1, Power2, Power3, Power4, Linear } from 'gsap';

// Make Hammer and GSAP available globally for PinchZoomer
window.Hammer = Hammer;
window.TweenMax = gsap;
window.TweenLite = gsap;

// GSAP 2.x compatibility - expose easing functions globally
window.Power0 = Power0;
window.Power1 = Power1;
window.Power2 = Power2;
window.Power3 = Power3;
window.Power4 = Power4;
window.Linear = Linear;

// Load PinchZoomer plugin AFTER globals are set (require is not hoisted like import)
require('./jquery.pinchzoomer.min.js');

// Re-initialize function for dynamically loaded content only.
// The PinchZoomer jQuery plugin already auto-initializes elements present
// on $(document).ready(), so this function should only be called for
// elements added to the DOM after initial page load (e.g. via AJAX).
window.initPinchZoomer = () => {
  if (typeof window.PinchZoomer !== 'undefined' && window.$ && window.$.fn.pinchzoomer) {
    const elements = document.querySelectorAll('[data-elem="pinchzoomer"]');

    elements.forEach(element => {
      // Check if already initialized by looking for a controlHolder sibling
      // (PinchZoomer appends the controlHolder to the parent, not inside the element)
      const parent = element.parentElement;
      const alreadyInitialized = parent && parent.querySelector(':scope > .controlHolder');

      if (!alreadyInitialized) {
        window.$(element).pinchzoomer();
      }
    });
  }
};

/**
 * Post-init setup for floorplan containers on server-rendered pages.
 *
 * PinchZoomer auto-initializes on $(document).ready(). By window load,
 * the controlHolder has been appended to the wrapper by PinchZoomer.
 * We move it to the container so it isn't clipped by the wrapper's
 * forced overflow:hidden, set up a fullscreen observer, and calculate
 * the container height based on the image aspect ratio.
 */
window.addEventListener('load', () => {
  const containers = document.querySelectorAll('[data-floorplan-container]');

  containers.forEach(container => {
    const wrapper = container.querySelector('[data-floorplan-wrapper]');
    if (!wrapper) return;

    // Move controlHolder from wrapper to container so it isn't clipped.
    const controlHolder = wrapper.querySelector('.controlHolder');
    if (controlHolder) {
      container.appendChild(controlHolder);

      // Watch for fullscreen toggle on the wrapper.
      new MutationObserver(() => {
        const isFullscreen = wrapper.classList.contains('fullscreenDiv');
        const currentParent = controlHolder.parentElement;
        if (isFullscreen && currentParent !== wrapper) {
          wrapper.appendChild(controlHolder);
        } else if (!isFullscreen && currentParent !== container) {
          container.appendChild(controlHolder);
        }
      }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    }

    // Calculate and set the container height.
    initFloorplanContainerHeight(container);
  });
});

/**
 * Calculate and set a floorplan container's height based on the image
 * aspect ratio, viewport constraints, and parent constraints.
 *
 * @param {HTMLElement} floorplanContainer - The [data-floorplan-container] element
 */
function initFloorplanContainerHeight(floorplanContainer) {
  const floorplanImage = floorplanContainer.querySelector('.zoomHolder img');
  if (!floorplanImage) return;

  const calculateHeight = () => {
    if (floorplanContainer.classList.contains('fullscreenDiv')) {
      floorplanContainer.style.height = '';
      return;
    }

    // Skip JS height calculation on mobile — CSS handles sizing
    // via aspect-ratio + max-height on the parent wrapper.
    if (window.innerWidth < 768) {
      floorplanContainer.style.height = '';
      return;
    }

    let naturalWidth = floorplanImage.naturalWidth;
    let naturalHeight = floorplanImage.naturalHeight;

    if (!naturalWidth || !naturalHeight) {
      const rect = floorplanImage.getBoundingClientRect();
      naturalWidth = rect.width;
      naturalHeight = rect.height;
    }

    if (!naturalWidth || !naturalHeight) return;

    const aspectRatio = naturalHeight / naturalWidth;
    const styles = window.getComputedStyle(floorplanContainer);
    const padL = parseFloat(styles.paddingLeft) || 0;
    const padR = parseFloat(styles.paddingRight) || 0;
    const padT = parseFloat(styles.paddingTop) || 0;
    const padB = parseFloat(styles.paddingBottom) || 0;

    const parentEl = floorplanContainer.parentElement;
    const parentStyles = parentEl ? window.getComputedStyle(parentEl) : null;
    const parentMaxH = parentStyles ? parseFloat(parentStyles.maxHeight) : NaN;
    const parentAR = parentStyles ? parentStyles.aspectRatio : 'auto';
    const hasParentConstraint = !isNaN(parentMaxH) || (parentAR && parentAR !== 'auto');
    const parentH = parentEl ? parentEl.offsetHeight : Infinity;
    const maxContainerH = hasParentConstraint
      ? (!isNaN(parentMaxH) ? Math.min(parentMaxH, parentH) : parentH)
      : Infinity;

    const availW = floorplanContainer.offsetWidth - padL - padR;
    const effectiveW = availW * 0.9;
    const idealH = effectiveW * aspectRatio;

    const vpH = window.innerHeight;
    const topOff = floorplanContainer.getBoundingClientRect().top;
    const vpContentH = vpH - topOff - 32 - padT - padB;
    const parentContentH = maxContainerH - padT - padB;

    const useVP = topOff >= 0 && topOff < vpH && vpContentH >= idealH * 0.3;
    const maxContentH = useVP ? Math.min(vpContentH, parentContentH) : parentContentH;

    let finalImgH;
    if (maxContentH >= idealH) finalImgH = idealH;
    else if (maxContentH > 0) finalImgH = maxContentH;
    else finalImgH = idealH;

    let finalH = finalImgH + padT + padB;
    if (!isNaN(parentMaxH) && finalH > parentMaxH) finalH = parentMaxH;

    floorplanContainer.style.height = `${finalH}px`;
  };

  const calculateWhenReady = () => {
    if (floorplanImage.complete) {
      calculateHeight();
    } else {
      floorplanImage.addEventListener('load', () => requestAnimationFrame(calculateHeight));
    }
  };

  requestAnimationFrame(calculateWhenReady);

  // Recalculate on resize
  let raf;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      if (document.contains(floorplanContainer)) calculateHeight();
    });
  });

  // Recalculate on fullscreen class change
  new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.type === 'attributes' && m.attributeName === 'class') calculateHeight();
    });
  }).observe(floorplanContainer, { attributes: true, attributeFilter: ['class'] });
}
